import { Injectable } from '@angular/core';
import { GqlServiceV2 } from '../../content/service/gql.service-v2';
import {
    CompareDonorFieldObject,
    CompareDonorInput,
    DonorActivityCreateInput,
    DonorActivityDeleteInput,
    DonorActivityGridObject,
    DonorActivityObject,
    MergeDonorInput,
    OkObject,
    PersonGridObject,
} from '@it2go/types';
import { DonorImportInput } from '@libs/types/src/chcrm/input-type/donor/donor-import.input';
import { GridInput } from '@it2go/data-grid';
import {
    donorGridItemDetailFrag,
    donorGridItemFrag,
    donorGridItemSearchFrag,
    donorStatisticsFrag,
} from '../../../services/types/donor.types';
import { filterFrag } from '../../../services/types/filter.types';
import { DonorCreateInput } from '@libs/types/src/chcrm/input-type/donor/donor-create.input';
import {
    DonorBaseUpdateInput,
    DonorUpdateInput,
} from '@libs/types/src/chcrm/input-type/donor/donor-update.input';
import {
    donorActivityFrag,
    donorActivityGridItemFrag,
} from '../../../services/types/donor-activity.types';
import { DonorDeleteInput } from '@libs/types/src/chcrm/input-type/donor/donor-delete.input';
import { DonorCategorySyncInput } from '@libs/types/src/chcrm/input-type/donor-category/donor-category-sync.input';
import { CollectionCategoryObject } from '@libs/types/src/chcrm/object-type/collection/collection-category.object';
import { DonorGetInput } from '@libs/types/src/chcrm/input-type/donor/donor-get.input';
import { DonorStatisticsObject } from '@libs/types/src/chcrm/object-type/donor/donor-statistics.object';

@Injectable({ providedIn: 'root' })
export class DonorGqlService extends GqlServiceV2 {
    getDonors(input: GridInput | null, frag: string = donorGridItemFrag) {
        return this.query<PersonGridObject>(
            `
                query($input: GridInput) {
                    donor {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    createDonor(input: DonorCreateInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: DonorCreateInput!) {
                    donor {
                        create(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    updateDonor(input: DonorUpdateInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: DonorUpdateInput!) {
                    donor {
                        update(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    updateDonorPartial(input: DonorBaseUpdateInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: DonorBaseUpdateInput!) {
                    donor {
                        updatePartial(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    getDetailDonors(input: GridInput | null, frag: string = donorGridItemDetailFrag) {
        return this.query<PersonGridObject>(
            `
                query($input: GridInput) {
                    donor {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    getSearchDonor(input: GridInput | null, frag: string = donorGridItemSearchFrag) {
        return this.query<PersonGridObject>(
            `
                query($input: GridInput) {
                    donor {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    compare(input: CompareDonorInput) {
        return this.query<CompareDonorFieldObject[]>(
            `
                query($input: CompareDonorInput!) {
                    donor {
                        compare(input: $input) {
                            key
                            left
                            right
                        }
                    }
                }
            `,
            { input },
        );
    }

    merge(input: MergeDonorInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: MergeDonorInput!) {
                    donor {
                        merge(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    getDonorActivities(input: GridInput | null, frag: string = donorActivityGridItemFrag) {
        return this.query<DonorActivityGridObject>(
            `
                query ($input: GridInput) {
                    donorActivity {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    createDonorActivity(input: DonorActivityCreateInput, frag: string = donorActivityFrag) {
        return this.mutate<DonorActivityObject>(
            `
                mutation ($input: DonorActivityCreateInput!) {
                    donorActivity {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    deleteDonorActivity(input: DonorActivityDeleteInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: DonorActivityDeleteInput!) {
                    donorActivity {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    deleteDonor(input: DonorDeleteInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: DonorDeleteInput!) {
                    donor {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    updateCategories(input: DonorCategorySyncInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: DonorCategorySyncInput!) {
                    donorCategory {
                        update(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    getDonorCategories() {
        return this.query<CollectionCategoryObject[]>(
            `
                query {
                    donorCategory {
                        list {
                            id
                            category
                        }
                    }
                }
            `,
        );
    }

    getStatistics(input: DonorGetInput, frag: string = donorStatisticsFrag) {
        return this.query<DonorStatisticsObject>(
            `
                query($input: DonorGetInput!) {
                    donor {
                        statistics(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public importDonorCsv(input: DonorImportInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: DonorImportInput!) {
                    donor {
                        importCsv(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }
}
